/**
 * SharePage - Public page for viewing shared documents
 *
 * Does not require authentication.
 * Supports both flat document lists and folder hierarchies.
 *
 * State management is handled by ShareStore (MST).
 */

import { useParams } from "react-router-dom";
import { observer } from "mobx-react-lite";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Loader2,
  AlertCircle,
  FileText,
  Clock,
  Link as LinkIcon,
  FolderOpen,
  ChevronRight,
  Download,
} from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { useShareStore } from "@/hooks/useShareStore";
import { DocumentListTable } from "@/domains/documents/components/organisms/DocumentListTable";
import { DocumentViewerDialog } from "@/domains/documents/components/DocumentViewerDialog";
import { SidebarFolderItem } from "./share/components/SidebarFolderItem";
import { cn } from "@/lib/utils";

/**
 * Public page for viewing shared documents.
 * Wrapped with observer for MobX reactivity.
 */
const SharePage = observer(function SharePage() {
  const { token } = useParams<{ token: string }>();
  const { toast } = useToast();
  const store = useShareStore(token);

  // Loading state
  if (store.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">Loading shared documents...</p>
        </div>
      </div>
    );
  }

  // Error state
  if (store.error || !store.shareDetails) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardContent className="flex flex-col items-center gap-4 pt-6">
            <AlertCircle className="h-12 w-12 text-destructive" />
            <h2 className="text-xl font-semibold">Unable to Load Share</h2>
            <p className="text-center text-muted-foreground">
              {store.error || "This share link is invalid or has expired."}
            </p>
            <Button variant="outline" onClick={() => token && store.loadShare(token)}>
              Try Again
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { shareDetails, hasFolders, breadcrumbPath, currentFolderId } = store;

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 py-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="mb-2 flex items-center gap-2">
                <LinkIcon className="h-5 w-5 text-primary" />
                <h1 className="text-xl font-semibold">
                  {shareDetails.title || "Shared Documents"}
                </h1>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                <span className="flex items-center gap-1">
                  <FileText className="h-4 w-4" />
                  {shareDetails.documentCount}{" "}
                  {shareDetails.documentCount === 1 ? "document" : "documents"}
                </span>
                {shareDetails.folderCount > 0 && (
                  <span className="flex items-center gap-1">
                    <FolderOpen className="h-4 w-4" />
                    {shareDetails.folderCount}{" "}
                    {shareDetails.folderCount === 1 ? "folder" : "folders"}
                  </span>
                )}
                <span
                  className={`flex items-center gap-1 ${store.isExpiringSoon ? "text-amber-600" : ""}`}
                >
                  <Clock className="h-4 w-4" />
                  Expires{" "}
                  {store.expiresAt && formatDistanceToNow(store.expiresAt, { addSuffix: true })}
                </span>
              </div>
            </div>
            {/* Download button */}
            <Button
              onClick={() => store.downloadSelected(toast)}
              disabled={store.isDownloading || shareDetails.documentCount === 0}
            >
              {store.isDownloading ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Download className="mr-2 h-4 w-4" />
              )}
              {store.hasSelection
                ? `Download Selected (${store.selectedCount + store.selectedFolderIds.length})`
                : "Download All"}
            </Button>
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="container mx-auto px-4 py-6">
        <div className="rounded-lg border bg-background p-4">
          {/* Breadcrumb / title row */}
          <div className="mb-4 flex items-center">
            {/* Left side: Share title / folder path */}
            <div className="flex min-w-0 flex-1 items-center">
              <button
                onClick={() => store.navigateToFolder(null)}
                className={cn(
                  "text-lg font-semibold hover:text-slate-700",
                  currentFolderId ? "text-slate-500" : "text-slate-900"
                )}
              >
                {shareDetails.title || "Shared Documents"}
              </button>

              {/* Folder breadcrumb path */}
              {breadcrumbPath.map((folder) => (
                <span key={folder.id} className="flex items-center">
                  <ChevronRight className="mx-1.5 h-4 w-4 flex-shrink-0 text-slate-400" />
                  {folder.id === currentFolderId ? (
                    <span className="text-lg font-semibold text-slate-900">{folder.name}</span>
                  ) : (
                    <button
                      onClick={() => store.navigateToFolder(folder.id)}
                      className="text-lg font-semibold text-slate-500 hover:text-slate-700"
                    >
                      {folder.name}
                    </button>
                  )}
                </span>
              ))}
            </div>
          </div>

          {/* Two-column layout when folders exist */}
          <div className={cn("flex gap-4", hasFolders && "flex-col lg:flex-row")}>
            {/* Left sidebar - folder tree */}
            {hasFolders && (
              <aside className="w-full shrink-0 lg:w-56">
                {/* All documents link */}
                <div
                  className={cn(
                    "mb-1 flex cursor-pointer items-center gap-2 rounded-md px-2 py-1 text-sm transition-colors",
                    !currentFolderId ? "bg-primary/10 text-primary" : "hover:bg-muted"
                  )}
                  onClick={() => store.navigateToFolder(null)}
                >
                  <FolderOpen
                    className={cn(
                      "h-4 w-4",
                      !currentFolderId ? "text-primary" : "text-muted-foreground"
                    )}
                  />
                  <span className="flex-1">All Documents</span>
                  <span className="text-xs text-muted-foreground">
                    {shareDetails.documentCount}
                  </span>
                </div>

                {/* Folder tree — sorted A–Z to match the projects folder tree */}
                {store.sortedFolderTree.map((folder) => (
                  <SidebarFolderItem
                    key={folder.id}
                    folder={folder}
                    currentFolderId={currentFolderId}
                    onFolderSelect={store.navigateToFolder}
                  />
                ))}
              </aside>
            )}

            {/* Main content - unified document/folder list */}
            <div className="flex-1">
              {store.tableDocuments.length === 0 && store.currentSubfolders.length === 0 ? (
                <div className="flex flex-col items-center gap-4 py-12">
                  <FileText className="h-12 w-12 text-muted-foreground" />
                  <p className="text-muted-foreground">
                    {currentFolderId ? "No documents in this folder" : "No documents in this share"}
                  </p>
                </div>
              ) : (
                <DocumentListTable
                  documents={store.tableDocuments}
                  folders={store.currentSubfolders}
                  store={store as never}
                  showCheckbox={true}
                  hideBulkActionBar={true}
                  showVersion={false}
                  showDateCreated={false}
                  showStatus={false}
                  showUser={false}
                  showTags={false}
                  showParent={false}
                  showDeleteAction={false}
                  showActions={true}
                  deleteLabel="Download"
                  onDownloadOverride={(docId) => store.downloadDocument(docId, toast)}
                  onPreviewOverride={(docId) => store.openPreview(docId, toast)}
                  onFolderClick={store.navigateToFolder}
                  selectedFolderIds={[...store.selectedFolderIds]}
                  onFolderSelect={(folderId, isSelected) =>
                    store.selectFolder(folderId, isSelected)
                  }
                  disableChildModal={true}
                />
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t bg-card py-4">
        <div className="container mx-auto px-4">
          <p className="text-center text-xs text-muted-foreground">
            Shared via What-If Document Management
          </p>
        </div>
      </footer>

      {/* Document preview dialog */}
      <DocumentViewerDialog
        document={store.previewDocument}
        blobUrl={store.previewBlobUrl}
        blobData={store.previewBlobData}
        open={store.previewOpen}
        onOpenChange={(open) => store.setPreviewOpen(open)}
        onDownloadOverride={
          store.previewBlobUrl && store.previewDocument
            ? () => {
                const blobUrl = store.previewBlobUrl;
                const previewDocument = store.previewDocument;
                if (!blobUrl || !previewDocument) return;
                const link = document.createElement("a");
                link.href = blobUrl;
                link.download = previewDocument.filename;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
              }
            : undefined
        }
      />
    </div>
  );
});

export default SharePage;
